import React from 'react';
import { ComposedChart, Line, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import CountUp from 'react-countup';
import { ChevronLeft, ChevronRight, TrendingUp, ShoppingCart, Euro } from 'lucide-react';
import { useLocale } from '../../context/LocaleContext';

type DataPoint = { date: string; conversionRate: number; orders: number };

interface SalesPerformanceKPIProps {
  conversionRate: number;
  deltaPct?: number;
  orders: number;
  ordersDelta?: number;
  avgOrderValue: number;
  avgOrderValueDelta?: number;
  series: DataPoint[];
  title?: string;
  dateRange?: string;
  onPrevious?: () => void;
  onNext?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  onReturnToPresent?: () => void;
  isAtPresent?: boolean;
}

export function SalesPerformanceKPI({
  conversionRate,
  deltaPct,
  orders,
  ordersDelta,
  avgOrderValue,
  avgOrderValueDelta,
  series,
  title = "Sales Conversion",
  dateRange,
  onPrevious,
  onNext,
  canGoPrevious = true,
  canGoNext = true,
  onReturnToPresent,
  isAtPresent = true,
}: SalesPerformanceKPIProps) {
  const { formatDate } = useLocale();
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const formattedDate = formatDate(data.date, { 
        day: 'numeric', 
        month: 'short' 
      });
      
      return (
        <div className="dark:bg-surface bg-white border dark:border-border-color border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-xs dark:text-text-secondary text-gray-600 mb-2">{formattedDate}</p>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-primary">
              {data.conversionRate.toFixed(1)}% conversion rate
            </p>
            <p className="text-xs dark:text-text-secondary text-gray-600">
              {data.orders} orders
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col items-center w-full h-full">
      <p className="text-m font-semibold dark:text-text-primary text-light-text-primary mb-3">{title}</p>
      
      <div className="rounded-2xl border dark:border-border-color border-light-border-color dark:bg-surface bg-light-surface p-4 space-y-3 w-full">
        {/* Main Conversion Rate */}
        <div className="flex items-start justify-between">
          <div className="flex flex-col items-start">
            <div className="text-xs uppercase tracking-wider dark:text-text-secondary text-light-text-secondary">
              Conversion Rate (30d)
            </div>
            <div className="text-2xl font-semibold dark:text-white text-gray-900">
              <CountUp
                end={conversionRate}
                duration={0.8}
                decimals={1}
                decimal="."
                suffix="%"
              />
            </div>
          </div>
          {typeof deltaPct === 'number' && (
            <span
              className={`text-xs rounded-full px-2 py-1 font-medium ${
                deltaPct >= 0
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300'
                  : 'bg-rose-500/15 text-rose-600 dark:text-rose-300'
              }`}
            >
              {deltaPct >= 0 ? '↑' : '↓'} {Math.abs(deltaPct).toFixed(1)}%
            </span>
          )}
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 dark:bg-background/50 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-1.5 mb-1">
              <ShoppingCart className="w-3 h-3 dark:text-text-secondary text-light-text-secondary" />
              <p className="text-[10px] uppercase tracking-wider dark:text-text-secondary text-light-text-secondary">
                Orders
              </p>
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-lg font-bold dark:text-white text-gray-900">{orders}</p>
              {typeof ordersDelta === 'number' && (
                <span
                  className={`text-[10px] font-medium ${
                    ordersDelta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                  }`}
                >
                  {ordersDelta >= 0 ? '↑' : '↓'} {Math.abs(ordersDelta).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
          
          <div className="p-3 dark:bg-background/50 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-1.5 mb-1">
              <Euro className="w-3 h-3 dark:text-text-secondary text-light-text-secondary" />
              <p className="text-[10px] uppercase tracking-wider dark:text-text-secondary text-light-text-secondary">
                Avg Order Value
              </p>
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-lg font-bold dark:text-white text-gray-900">€{avgOrderValue.toFixed(0)}</p>
              {typeof avgOrderValueDelta === 'number' && (
                <span
                  className={`text-[10px] font-medium ${
                    avgOrderValueDelta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                  }`}
                >
                  {avgOrderValueDelta >= 0 ? '↑' : '↓'} {Math.abs(avgOrderValueDelta).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="h-24 mt-2">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series}>
              <defs>
                <linearGradient id="conversionGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="currentColor" 
                className="dark:text-gray-700 text-gray-200"
                vertical={false}
              />
              <XAxis 
                dataKey="date" 
                hide 
              />
              <YAxis 
                yAxisId="left"
                hide 
              />
              <YAxis 
                yAxisId="right"
                hide 
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }} />
              
              {/* Bars for orders */}
              <Bar
                yAxisId="right"
                dataKey="orders"
                fill="url(#conversionGradient)"
                radius={[4, 4, 0, 0]}
                maxBarSize={20}
              />
              
              {/* Line for conversion rate */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="conversionRate"
                stroke="#10B981"
                strokeWidth={2.5}
                dot={{ fill: '#10B981', r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: '#10B981', strokeWidth: 2, stroke: '#fff' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Date Navigation */}
        {dateRange && (
          <div className="flex items-center justify-center gap-4 pt-2 border-t dark:border-border-color/50 border-gray-200">
            <button
              onClick={onPrevious}
              disabled={!canGoPrevious}
              className={`p-1 rounded transition-colors ${
                !canGoPrevious
                  ? 'dark:text-text-secondary/30 text-light-text-secondary/30 cursor-not-allowed'
                  : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-text-primary hover:text-light-text-primary'
              }`}
              aria-label="Previous week"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <div className="text-center min-w-[140px]">
              <p className="text-xs dark:text-text-secondary text-light-text-secondary">
                {dateRange}
              </p>
              {!isAtPresent && onReturnToPresent && (
                <button
                  onClick={onReturnToPresent}
                  className="text-[10px] text-primary hover:text-primary-hover underline mt-0.5"
                >
                  Today
                </button>
              )}
            </div>
            
            <button
              onClick={onNext}
              disabled={!canGoNext}
              className={`p-1 rounded transition-colors ${
                !canGoNext
                  ? 'dark:text-text-secondary/30 text-light-text-secondary/30 cursor-not-allowed'
                  : 'dark:text-text-secondary text-light-text-secondary dark:hover:bg-gray-700 hover:bg-gray-100 dark:hover:text-text-primary hover:text-light-text-primary'
              }`}
              aria-label="Next week"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
